import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  CACHE_KEYS,
  CreateOrUpdateInvestmentParams,
  Investment,
  useEditInvestment,
  useGetPrivateNotes,
  useUpdatePrivateNotes,
} from "@bn/api/apiv1";
import { useGetCurrencies } from "@api/v1/hooks/useCurrencies";
import { GET_INVESTMENT_KEY } from "@api/cacheKeys";
import { InvestmentEditAdvancedTabContainer } from "./InvestmentEditAdvancedTabContainer";

interface Props {
  investment: Investment;
}

export const InvestmentEditAdvancedTab = ({ investment }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: currenciesResponse } = useGetCurrencies({ type: "standard" });
  const { mutate: update, isPending, error } = useEditInvestment();
  const { data: privateNotes, isLoading: arePrivateNotesLoading } =
    useGetPrivateNotes({ id: investment.id });
  const { mutateAsync: updateNotes, isPending: isSavingNotes } =
    useUpdatePrivateNotes({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.GET_PRIVATE_NOTES],
        });
      },
      onError: () => {
        enqueueSnackbar(t("investmentEditPage.advancedTab.commentSaveError"), {
          autoHideDuration: 5000,
          variant: "error",
        });
      },
    });

  const initialComment = privateNotes?.comment ?? "";

  const handleSaveClick = (
    data: CreateOrUpdateInvestmentParams,
    comment: string
  ) => {
    update(
      {
        id: investment.id,
        createOrUpdateInvestmentParams: data,
      },
      {
        onSuccess: async () => {
          if (comment !== initialComment) {
            try {
              await updateNotes({
                id: investment.id,
                createOrUpdateCommentParams: { comment },
              });
            } catch {
              // note save error is surfaced via onError; the investment edit
              // itself already succeeded, so keep the save flow intact
            }
          }
          enqueueSnackbar(
            `${t("investmentEditPage.basicTab.saved")}: ${investment.name}`,
            { autoHideDuration: 3000, variant: "success" }
          );
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [GET_INVESTMENT_KEY] }),
            queryClient.invalidateQueries({
              queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
            }),
          ]);
        },
      }
    );
  };

  return (
    <InvestmentEditAdvancedTabContainer
      investment={investment}
      currencies={currenciesResponse?.currencies ?? []}
      isSaving={isPending || isSavingNotes}
      error={error}
      initialComment={initialComment}
      arePrivateNotesLoading={arePrivateNotesLoading}
      onSave={handleSaveClick}
    />
  );
};
